import './MultiItemInputModal.scss';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AutoComplete,
  Button,
  Divider,
  IconButton,
  Icons,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PendingContent,
  Spinner,
} from 'plume-ui';
import { AutoCompleteResultsItem } from 'plume-ui/dist/components/AutoComplete/AutoCompleteResults';
import { ModalStyles } from 'plume-ui/dist/components/Modal/Modal';

type MultiItemInputModalProps = {
  allowedValues: string[];
  loading?: boolean;
  onRequestClose?: (selectedItems: string[]) => void;
  open: boolean;
  title: string;
  values?: string[];
  limitSelection?: number;
};

const MultiItemInputModal = ({
  allowedValues,
  loading = false,
  onRequestClose,
  open,
  title,
  values,
  limitSelection,
}: MultiItemInputModalProps) => {
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [initialItems, setInitialItems] = useState<string[]>([]);
  const [searchHistory, setSearchHistory] = useState<{
    [key: string]: AutoCompleteResultsItem[];
  }>({});

  useEffect(() => {
    if (values) {
      setSelectedItems(values);
      setInitialItems(values);
    }
  }, [values]);

  const handleCancel = () => {
    setSelectedItems(initialItems);
    onRequestClose?.(initialItems);
  };

  const handleSave = () => {
    setInitialItems(selectedItems);
    onRequestClose?.(selectedItems);
  };

  const getData = async (query: string): Promise<AutoCompleteResultsItem[]> => {
    if (query.length === 0) {
      return allowedValues
        .filter((item) => !selectedItems.includes(item))
        .map((item) => ({ id: item, label: item }));
    }

    query = query.toLowerCase();
    const history = { ...searchHistory };

    const historyKeys = Object.keys(searchHistory);
    historyKeys
      .filter((key) => !query.includes(key))
      .forEach((key) => delete history[key]);

    let data: AutoCompleteResultsItem[] = [];

    // if the exact query was already searched, reuse the results
    if (history[query]) {
      data = history[query];
    } else {
      // check for subset of queried results in history
      // i.e. 'abc' can be searched from 'ab' results
      // find the smallest subset to search from
      const similarPastQuery = Object.keys(history)
        .filter((pastQuery) => query.includes(pastQuery))
        .sort((a, b) => b.length - a.length)[0];

      if (similarPastQuery) {
        // If modified query is a substring of a past query, reuse the results
        data = history[similarPastQuery].filter((item) =>
          item.label.toLowerCase().includes(query),
        );
      } else {
        // Otherwise, filter from all values
        data = allowedValues
          .filter(
            (item: any) =>
              item.toLowerCase().includes(query) &&
              !selectedItems.includes(item),
          )
          .map((item: any) => ({ id: item, label: item.toString() }));
      }
    }

    setSearchHistory({ ...history, [query]: data });

    return data;
  };

  return (
    <Modal
      classes={(current: ModalStyles) => ({
        ...current,
        root: `${current.root} MultiItemInputModal`,
      })}
      isOpen={open}
      onRequestClose={handleCancel}
    >
      <ModalHeader title={title} />
      <ModalBody>
        <PendingContent loading={loading} hideContent loader={Spinner}>
          <div className="MultiItemInputModal__selectSection">
            <div className="MultiItemInputModal__headline">
              <div className="MultiItemInputModal__selectedCount">
                {t('multiItemInputModal.selectedFrom', {
                  selected: selectedItems.length,
                  total: allowedValues.length,
                })}
              </div>
              <Button
                animateLayout="size"
                styleVariant="tertiary"
                onClick={() => setSelectedItems([])}
              >
                {t('multiItemInputModal.clearAll')}
              </Button>
            </div>
            {selectedItems.length > 0 && (
              <div className="MultiItemInputModal__selectedItems">
                {selectedItems.map((item, i) => (
                  <div key={i} className="MultiItemInputModal__selectedItem">
                    {item}
                    <IconButton
                      onClick={() =>
                        setSelectedItems((selectedItems) =>
                          selectedItems.filter((oldItem) => oldItem !== item),
                        )
                      }
                    >
                      <Icons.CrossIcon width={24} height={24} />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
            <Divider orientation="horizontal" />
            {limitSelection && selectedItems.length >= limitSelection ? (
              <div className="MultiItemInputModal__selectionLimit">
                {t('monitorAdd.multiStringInputModal.numSelectionsAllowed', {
                  count: limitSelection,
                })}
              </div>
            ) : (
              <AutoComplete
                onSearch={getData}
                configuration={{
                  clearIcon: false,
                  displayFilters: false,
                  icon: <Icons.SearchIcon />,
                  clearQuery: true,
                  maxHeight: 240,
                  openInPortal: true,
                  minCharactersToSearch: 0,
                  maxItemsToShow: 500,
                  maxItemsReachedLabel: t(
                    'monitorAdd.multiStringInputModal.maxItemsReached',
                  ),
                  noResultsText: t('noResults'),
                  placeholderText: t(
                    'monitorAdd.multiStringInputModal.enterTextToFilter',
                  ),
                }}
                onItemClick={(item) => {
                  if (!limitSelection || selectedItems.length < limitSelection)
                    setSelectedItems([...selectedItems, item.id]);
                }}
              />
            )}
          </div>
        </PendingContent>
      </ModalBody>
      <ModalFooter>
        <Button
          animateLayout={'size'}
          onClick={handleCancel}
          styleVariant="tertiary"
        >
          {t('cancel')}
        </Button>
        <Button
          animateLayout={'size'}
          onClick={handleSave}
          styleVariant="superprimary"
        >
          {t('multiItemInputModal.save')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default memo(MultiItemInputModal);
